// Referrals: referrals for service from My Aged Care.
//
// A provider must accept, waitlist or reject each referral in the My Aged Care Service and Support
// Portal within 3 calendar days. Rostery cannot act in the portal, so this is the provider's record
// of it, as CareMaster does it: import the client record snapshots (RTF) or printed referrals, check
// what was read, add them to the register, and record each decision. Accepting adds the participant
// (or links the one already here) and enrols a CHSP referral in CHSP for the referred services.
// The reading rules live in backend/utils/macReferral.js.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import clsx from 'clsx';
import api from '../../utils/api';
import { isoDate } from '../../utils/time';
import toast from 'react-hot-toast';
import { PersonAvatar } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Inbox, Upload, FileText, Sparkles, UserPlus, Check, X, AlertTriangle, Download, Link2, Trash2, PencilLine,
  ListPlus, Hourglass, Ban, Undo2, Loader2, ChevronDown, ChevronUp, CheckCircle2,
} from 'lucide-react';
import {
  PageHead, Register, FigureStrip, Figure, Field, FormRow, DetailDrawer, Deadline, FilterChips, fmtDate,
} from './design';

const PROGRAM = { chsp: 'CHSP', sah: 'Support at Home' };
const STATUS = {
  new: ['To respond', 'badge-yellow'], accepted: ['Accepted', 'badge-green'], waitlisted: ['Waitlisted', 'badge-blue'],
  rejected: ['Rejected', 'badge-red'], revoked: ['Revoked', 'badge-gray'], recalled: ['Recalled', 'badge-gray'],
};
const PRIORITY = { high: 'text-red-800 font-medium', medium: 'text-amber-800', low: 'text-slate-600' };
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const ACCEPT_FILES = '.rtf,.pdf,.docx,.txt,.htm,.html,.jpg,.jpeg,.png,.webp';
const MAX_FILES = 20;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const nameOf = (f) => [f.firstName, f.lastName].filter(Boolean).join(' ') || 'No name found';

function StatusBadge({ status }) {
  const [word, cls] = STATUS[status] || [cap(status || ''), 'badge-gray'];
  return <span className={clsx('badge', cls)}>{word}</span>;
}

// Fields from a register row, in the shape the form and the server use.
const fieldsOf = (r) => ({
  ...(r.details?.fields || {}),
  firstName: r.first_name || '', lastName: r.last_name || '', dateOfBirth: r.date_of_birth || '', macId: r.mac_id || '',
  program: r.program || '', priority: r.priority || '', referredDate: r.referred_date || '', dueDate: r.due_date || '',
  recommendedStart: r.recommended_start || '', takeUpBy: r.take_up_by || '', referralCode: r.referral_code || '',
  referralId: r.referral_ref || '', napsServiceId: r.naps_service_id || '', serviceTypes: r.service_types || [],
  frequency: r.frequency || '', comments: r.comments || '',
});
// Empty strings travel as null, so a cleared box clears the value.
const forServer = (f) => {
  const out = {};
  for (const [k, v] of Object.entries(f || {})) out[k] = v === '' ? null : v;
  if (typeof out.serviceTypes === 'string') out.serviceTypes = out.serviceTypes.split(/[;,\n]/).map((x) => x.trim()).filter(Boolean);
  return out;
};

export default function AgedCareReferrals({ onGoTo }) {
  const [data, setData] = useState({ referrals: [], reasons: { reject: [], revoke: [] }, ai: false, today: isoDate() });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('open');
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/aged-care/referrals'); setData(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Could not load referrals'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data.referrals || [], [data.referrals]);
  const today = data.today || isoDate();
  const counts = useMemo(() => ({
    open: rows.filter((r) => ['new', 'waitlisted'].includes(r.status)).length,
    new: rows.filter((r) => r.status === 'new').length,
    overdue: rows.filter((r) => r.status === 'new' && r.due_date && r.due_date < today).length,
    waitlisted: rows.filter((r) => r.status === 'waitlisted').length,
    accepted: rows.filter((r) => r.status === 'accepted').length,
    closed: rows.filter((r) => ['rejected', 'revoked', 'recalled'].includes(r.status)).length,
    portal: rows.filter((r) => ['accepted', 'waitlisted', 'rejected', 'revoked'].includes(r.status) && !r.portal_done).length,
  }), [rows, today]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'open' && !['new', 'waitlisted'].includes(r.status)) return false;
      if (filter === 'accepted' && r.status !== 'accepted') return false;
      if (filter === 'closed' && !['rejected', 'revoked', 'recalled'].includes(r.status)) return false;
      if (filter === 'portal' && !(['accepted', 'waitlisted', 'rejected', 'revoked'].includes(r.status) && !r.portal_done)) return false;
      return !q || `${r.first_name} ${r.last_name} ${r.mac_id || ''} ${r.referral_code || ''}`.toLowerCase().includes(q);
    });
  }, [rows, filter, search]);

  const columns = [
    {
      key: 'name', header: 'Referred', sortable: true, sortValue: (r) => `${r.last_name} ${r.first_name}`,
      render: (r) => (
        <span className="min-w-0 flex items-center gap-2.5">
          <PersonAvatar id={r.client_id || r.id} name={`${r.first_name} ${r.last_name}`} size="sm" />
          <span className="min-w-0 block">
            <span className="font-medium text-slate-900 block truncate">{r.first_name} {r.last_name}</span>
            <span className="text-[12px] text-slate-500 block truncate">
              {[r.mac_id, r.date_of_birth ? `born ${fmtDate(r.date_of_birth)}` : null].filter(Boolean).join(' · ') || 'No My Aged Care ID'}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'program', header: 'Program', width: 150,
      render: (r) => (r.program ? <span className="text-slate-800">{PROGRAM[r.program]}</span> : <span className="text-amber-700">Not known</span>),
    },
    {
      key: 'service_types', header: 'Services', width: 230,
      render: (r) => (r.service_types?.length
        ? <span className="text-[13px] text-slate-700 line-clamp-2">{r.service_types.join(', ')}</span>
        : <span className="text-slate-400">—</span>),
    },
    { key: 'priority', header: 'Priority', width: 90, render: (r) => (r.priority ? <span className={PRIORITY[r.priority]}>{cap(r.priority)}</span> : <span className="text-slate-400">—</span>) },
    {
      key: 'due_date', header: 'Respond by', width: 190, sortable: true,
      render: (r) => (r.status === 'new'
        ? <Deadline date={r.due_date} />
        : <span className="text-[13px] text-slate-500 tabular-nums">{r.action_date ? `${STATUS[r.status]?.[0] || ''} ${fmtDate(r.action_date)}` : '—'}</span>),
    },
    {
      key: 'status', header: 'Status', width: 150,
      render: (r) => (
        <span className="flex flex-col items-start gap-1">
          <StatusBadge status={r.status} />
          {['accepted', 'waitlisted', 'rejected', 'revoked'].includes(r.status) && !r.portal_done && (
            <span className="text-[11.5px] text-amber-700">Not yet done in the portal</span>
          )}
          {r.portal_done && r.portal_done_at && (
            <span className="text-[11.5px] text-slate-500">Done in the portal {fmtDate(r.portal_done_at)}{r.portal_done_by_name ? ` by ${r.portal_done_by_name}` : ''}</span>
          )}
          {r.status === 'waitlisted' && (
            <span className="text-[11.5px] text-slate-500">Reviewed {r.waitlist_reviewed_at ? fmtDate(r.waitlist_reviewed_at) : 'not yet'}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHead icon={Inbox}
        title="Referrals"
        purpose="Referrals for service from My Aged Care. Accept, waitlist or reject each one in the My Aged Care portal within 3 calendar days, and record it here. Accepting adds the participant, and enrols a CHSP referral in CHSP."
        actions={(
          <>
            <button type="button" onClick={() => setAdding(true)} className="btn-secondary btn-sm"><ListPlus size={14} /> Add by hand</button>
            <button type="button" onClick={() => setImporting(true)} className="btn-primary btn-sm"><Upload size={14} /> Import from My Aged Care</button>
          </>
        )}
      />
      <FigureStrip>
        <Figure label="To respond to" value={counts.new} icon={Inbox} tone={counts.new ? 'amber' : 'slate'} sub={counts.new ? 'Within 3 calendar days' : 'Nothing waiting'} />
        <Figure label="Past the 3 days" value={counts.overdue} tone={counts.overdue ? 'red' : 'slate'} />
        <Figure label="Waitlisted" value={counts.waitlisted} icon={Hourglass} />
        <Figure label="Not yet done in the portal" value={counts.portal} tone={counts.portal ? 'amber' : 'slate'} sub={counts.portal ? 'Record them in My Aged Care' : null} />
      </FigureStrip>
      <Register
        title="Referrals" count={shown.length} icon={Inbox}
        search={search} onSearch={setSearch} searchPlaceholder="Search by name, My Aged Care ID or referral code"
        chips={(
          <FilterChips multi={false} value={filter} onChange={(v) => setFilter(v || 'all')} ariaLabel="Show referrals"
            options={[
              { value: 'open', label: 'To respond or waitlisted', count: counts.open },
              { value: 'accepted', label: 'Accepted', count: counts.accepted },
              { value: 'closed', label: 'Rejected, revoked or recalled', count: counts.closed },
              { value: 'portal', label: 'Not yet done in the portal', count: counts.portal, tone: counts.portal ? 'amber' : undefined },
              { value: 'all', label: 'All', count: rows.length },
            ]} />
        )}
        columns={columns} rows={shown} rowKey="id" loading={loading} error={error} onRetry={load}
        onRowClick={(r) => setOpenId(r.id)} activeRowKey={openId}
        rowTone={(r) => (r.status === 'new' && r.due_date && r.due_date < today ? 'red' : r.status === 'new' ? 'amber' : undefined)}
        empty={{
          icon: Inbox, title: filter === 'open' ? 'No referrals waiting' : 'No referrals here',
          hint: 'Import the client record snapshot (RTF) or a printed referral from the My Aged Care portal, up to 20 at a time.',
          action: <button type="button" onClick={() => setImporting(true)} className="btn-primary btn-sm"><Upload size={14} /> Import from My Aged Care</button>,
        }}
        footer="Rostery records your decisions; the decision itself is made in the My Aged Care Service and Support Portal, by a Team Leader."
      />

      {importing && <ImportDrawer aiAvailable={data.ai} onClose={() => { setImporting(false); load(); }} />}
      {adding && <AddDrawer onClose={(saved) => { setAdding(false); if (saved) { load(); setOpenId(saved); } }} />}
      {openId && (
        <ReferralDrawer id={openId} reasons={data.reasons} aiAvailable={data.ai} onGoTo={onGoTo}
          onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
}

// ── The details of a referral, to check or correct ───────────────────────────────────────────────
function ReferralForm({ value, onChange, idPrefix }) {
  const [more, setMore] = useState(false);
  const f = value;
  const set = (k) => (e) => onChange({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const services = typeof f.serviceTypes === 'string' ? f.serviceTypes : (f.serviceTypes || []).join(', ');
  const text = (k, label, props = {}) => (
    <Field label={label} htmlFor={`${idPrefix}-${k}`}>
      <input id={`${idPrefix}-${k}`} className="input-field" value={f[k] || ''} onChange={set(k)} {...props} />
    </Field>
  );
  const date = (k, label) => (
    <Field label={label} htmlFor={`${idPrefix}-${k}`}>
      <input id={`${idPrefix}-${k}`} type="date" className="input-field" value={f[k] || ''} onChange={set(k)} />
    </Field>
  );
  return (
    <div className="space-y-3.5">
      <FormRow cols={2}>
        {text('firstName', 'First name', { autoComplete: 'off' })}
        {text('lastName', 'Last name', { autoComplete: 'off' })}
        {date('dateOfBirth', 'Date of birth')}
        {text('macId', 'My Aged Care ID', { placeholder: 'AC12345678', autoComplete: 'off', spellCheck: false })}
      </FormRow>
      <FormRow cols={2}>
        <Field label="Program" htmlFor={`${idPrefix}-program`}>
          <select id={`${idPrefix}-program`} className="input-field" value={f.program || ''} onChange={set('program')}>
            <option value="">Not known yet</option>
            <option value="chsp">CHSP</option>
            <option value="sah">Support at Home</option>
          </select>
        </Field>
        <Field label="Priority" htmlFor={`${idPrefix}-priority`}>
          <select id={`${idPrefix}-priority`} className="input-field" value={f.priority || ''} onChange={set('priority')}>
            <option value="">—</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </Field>
        {date('referredDate', 'Referred on')}
        <Field label="Respond by" htmlFor={`${idPrefix}-dueDate`} hint={f.dueDateEstimated && f.dueDate ? '3 calendar days after it was referred.' : null}>
          <input id={`${idPrefix}-dueDate`} type="date" className="input-field" value={f.dueDate || ''} onChange={(e) => onChange({ ...f, dueDate: e.target.value, dueDateEstimated: false })} />
        </Field>
      </FormRow>
      <Field label="Services referred for" htmlFor={`${idPrefix}-services`} hint="Separate them with commas.">
        <input id={`${idPrefix}-services`} className="input-field" value={services} onChange={(e) => onChange({ ...f, serviceTypes: e.target.value })} />
      </Field>
      <button type="button" onClick={() => setMore((m) => !m)} className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:text-primary-800" aria-expanded={more}>
        {more ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {more ? 'Fewer details' : 'More details'}
      </button>
      {more && (
        <div className="space-y-3.5">
          <FormRow cols={2}>
            {text('preferredName', 'Preferred name')}
            {text('gender', 'Gender')}
            {text('phone', 'Phone', { inputMode: 'tel' })}
            {text('mobile', 'Mobile', { inputMode: 'tel' })}
          </FormRow>
          {text('email', 'Email', { type: 'email' })}
          {text('address', 'Street address')}
          <FormRow cols={3}>
            {text('suburb', 'Suburb')}
            <Field label="State" htmlFor={`${idPrefix}-state`}>
              <select id={`${idPrefix}-state`} className="input-field" value={f.state || ''} onChange={set('state')}>
                <option value="">—</option>{STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            {text('postcode', 'Postcode', { inputMode: 'numeric' })}
          </FormRow>
          <FormRow cols={2}>
            {text('language', 'Preferred language')}
            {text('countryOfBirth', 'Country of birth')}
          </FormRow>
          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={f.interpreterRequired === true} onChange={(e) => onChange({ ...f, interpreterRequired: e.target.checked })} /> Needs an interpreter
          </label>
          <FormRow cols={2}>
            {date('recommendedStart', 'Recommended start')}
            {date('takeUpBy', 'Take-up deadline')}
            {text('referralCode', 'Referral code', { spellCheck: false })}
            {text('napsServiceId', 'NAPS service ID', { spellCheck: false })}
          </FormRow>
          {f.program === 'sah' && text('classification', 'Classification', { inputMode: 'numeric', placeholder: 'For example 4' })}
          {text('frequency', 'Recommended frequency and intensity')}
          <Field label="Referral comments" htmlFor={`${idPrefix}-comments`}>
            <textarea id={`${idPrefix}-comments`} rows={3} className="input-field" value={f.comments || ''} onChange={set('comments')} />
          </Field>
        </div>
      )}
    </div>
  );
}

function Contacts({ contacts, onRemove }) {
  if (!contacts?.length) return null;
  return (
    <div>
      <p className="text-[12px] font-medium text-slate-600 mb-1">Contacts</p>
      <ul className="space-y-1">
        {contacts.map((c, i) => (
          <li key={`${c.name}-${i}`} className="flex items-start gap-2 text-[13px] text-slate-700">
            <span className="min-w-0 flex-1">
              <span className="font-medium text-slate-900">{c.name || 'No name'}</span>
              <span className="text-slate-500">{[c.relationship, c.role].filter(Boolean).map((x, j) => (j ? ` · ${x}` : ` (${x}`)).join('')}{c.relationship || c.role ? ')' : ''}</span>
              {(c.phone || c.email) && <span className="block text-[12px] text-slate-500">{[c.phone, c.email].filter(Boolean).join(' · ')}</span>}
            </span>
            {onRemove && (
              <button type="button" onClick={() => onRemove(i)} className="p-1 rounded text-slate-400 hover:text-red-700 hover:bg-red-50" aria-label={`Remove ${c.name || 'contact'}`}><X size={14} /></button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Warnings({ list }) {
  if (!list?.length) return null;
  return (
    <ul className="rounded-lg bg-amber-50 px-3 py-2 space-y-0.5 text-[12px] text-amber-800">
      {list.map((w) => <li key={w} className="flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {w}</li>)}
    </ul>
  );
}

// ── Import: read the files, check each, add them ─────────────────────────────────────────────────
function ImportDrawer({ aiAvailable, onClose }) {
  const [files, setFiles] = useState([]);
  const [useAi, setUseAi] = useState(true);
  const [reading, setReading] = useState(false);
  const [items, setItems] = useState(null);   // review cards
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const pick = (list) => {
    const all = [...files, ...Array.from(list || [])];
    if (all.length > MAX_FILES) toast.error(`Up to ${MAX_FILES} files at a time. The first ${MAX_FILES} are kept.`);
    setFiles(all.slice(0, MAX_FILES));
  };
  const toCard = (it) => ({
    ...it, form: { ...it.fields, serviceTypes: (it.fields.serviceTypes || []).join(', ') }, contacts: it.contacts || [],
    link: it.match && it.match.certain ? it.match.client_id : '', state: 'review', busy: false,
  });
  const readAi = async (id) => {
    setItems((p) => p.map((c) => (c.id === id ? { ...c, busy: 'ai' } : c)));
    try {
      const r = await api.post(`/aged-care/referrals/${id}/read-ai`);
      setItems((p) => p.map((c) => (c.id === id ? toCard(r.data.item) : c)));
    } catch (e) {
      toast.error(e.response?.data?.message || 'The AI could not read this file');
      setItems((p) => p.map((c) => (c.id === id ? { ...c, busy: false } : c)));
    }
  };
  const read = async () => {
    if (!files.length) { toast.error('Choose the files first'); return; }
    setReading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    try {
      const r = await api.post('/aged-care/referrals/read', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const cards = (r.data.items || []).map(toCard);
      setItems(cards);
      // Files the reader could not make sense of go to AI one at a time, when chosen.
      if (useAi && aiAvailable) {
        for (const c of cards.filter((x) => !x.readable && x.can_ai)) await readAi(c.id);
      }
    } catch (e) { toast.error(e.response?.data?.message || 'Could not read the files'); }
    finally { setReading(false); }
  };
  const update = (id, patch) => setItems((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const confirmOne = async (c) => {
    update(c.id, { busy: 'add' });
    try {
      await api.post(`/aged-care/referrals/${c.id}/confirm`, {
        fields: forServer(c.form), contacts: c.contacts, clientId: c.link || null, matchBy: c.link && c.match?.client_id === c.link ? c.match.by : 'person',
        edited: true,
      });
      update(c.id, { state: 'added', busy: false });
      return true;
    } catch (e) { toast.error(e.response?.data?.message || `Could not add ${nameOf(c.form)}`); update(c.id, { busy: false }); return false; }
  };
  const discard = async (c) => {
    update(c.id, { busy: 'discard' });
    try { await api.delete(`/aged-care/referrals/${c.id}`); update(c.id, { state: 'discarded', busy: false }); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not discard it'); update(c.id, { busy: false }); }
  };
  const ready = (items || []).filter((c) => c.state === 'review' && c.form.firstName && c.form.lastName);
  const addAll = async () => { let n = 0; for (const c of ready) if (await confirmOne(c)) n += 1; if (n) toast.success(`${n} referral${n === 1 ? '' : 's'} added`); };
  const left = (items || []).filter((c) => c.state === 'review').length;

  return (
    <DetailDrawer open onClose={onClose} icon={Upload} width="lg"
      title="Import from My Aged Care" subtitle={items ? `${items.length} file${items.length === 1 ? '' : 's'} read: check each before adding it` : 'Client record snapshots or printed referrals'}
      footer={items ? (
        <>
          <button type="button" onClick={onClose} className="btn-secondary">{left ? 'Close' : 'Done'}</button>
          {ready.length > 0 && <button type="button" onClick={addAll} className="btn-primary"><Check size={15} /> Add {ready.length === 1 ? 'it' : `all ${ready.length}`} to referrals</button>}
        </>
      ) : (
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="button" onClick={read} disabled={reading || !files.length} className="btn-primary">
            {reading ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} {reading ? 'Reading…' : `Read ${files.length || ''} file${files.length === 1 ? '' : 's'}`}
          </button>
        </>
      )}>
      {!items ? (
        <div className="space-y-4">
          <p className="text-[13px] text-slate-600">
            In the My Aged Care portal, open the client record from the referral and download the client record snapshot (RTF), or print the referral summary to PDF. Word documents, saved pages and photos work too. Up to {MAX_FILES} files at a time.
          </p>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files); }}
            className={clsx('rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors', drag ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-slate-50')}>
            <Upload size={22} className="mx-auto text-slate-400" aria-hidden="true" />
            <p className="mt-2 text-[13px] text-slate-600">Drop the files here, or</p>
            <button type="button" onClick={() => inputRef.current?.click()} className="btn-secondary btn-sm mt-2">Choose files</button>
            <input ref={inputRef} type="file" multiple accept={ACCEPT_FILES} className="sr-only" aria-label="Choose My Aged Care files"
              onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
          </div>
          {files.length > 0 && (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-[13px]">
                  <FileText size={14} className="text-slate-400 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-slate-800">{f.name}</span>
                  <span className="text-[12px] text-slate-500 tabular-nums">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                  <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="p-1 rounded text-slate-400 hover:text-red-700" aria-label={`Remove ${f.name}`}><X size={14} /></button>
                </li>
              ))}
            </ul>
          )}
          {aiAvailable && (
            <label className="flex items-start gap-2 text-[13px] text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
              <span>If a file cannot be read (a scan or a photo), read it with AI. <span className="text-slate-500">You check every detail before anything is added.</span></span>
            </label>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => <ReviewCard key={c.id} card={c} aiAvailable={aiAvailable} onChange={(patch) => update(c.id, patch)} onAdd={() => confirmOne(c)} onDiscard={() => discard(c)} onAi={() => readAi(c.id)} />)}
        </div>
      )}
    </DetailDrawer>
  );
}

function ReviewCard({ card: c, aiAvailable, onChange, onAdd, onDiscard, onAi }) {
  if (c.state !== 'review') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px]">
        {c.state === 'added' ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Trash2 size={15} className="text-slate-400" />}
        <span className="font-medium text-slate-800">{nameOf(c.form)}</span>
        <span className="text-slate-500">{c.state === 'added' ? 'added to referrals' : 'discarded'}</span>
        <span className="ml-auto text-[12px] text-slate-400 truncate">{c.file_name}</span>
      </div>
    );
  }
  const found = c.form.firstName && c.form.lastName;
  return (
    <section className={clsx('rounded-xl border p-4 space-y-3', found ? 'border-slate-200' : 'border-red-200')} aria-label={c.file_name}>
      <header className="flex flex-wrap items-center gap-2">
        <FileText size={15} className="text-slate-400" />
        <span className="font-medium text-slate-900 truncate max-w-[60%]">{c.file_name}</span>
        {c.source === 'ai'
          ? <span className="badge badge-blue inline-flex items-center gap-1"><Sparkles size={11} /> Read by AI</span>
          : <span className="badge badge-gray">Read by Rostery</span>}
        {!found && <span className="badge badge-red">Client not found</span>}
        {c.busy === 'ai' && <span className="inline-flex items-center gap-1 text-[12px] text-slate-500"><Loader2 size={13} className="animate-spin" /> Reading with AI…</span>}
      </header>
      {c.existing && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {c.existing.name} already has a referral here ({STATUS[c.existing.status]?.[0].toLowerCase()}, added {fmtDate(c.existing.added)}). Discard this one if it is the same referral.
        </p>
      )}
      <Warnings list={c.warnings} />
      {c.match && (
        <fieldset className="rounded-lg border border-slate-200 px-3 py-2">
          <legend className="px-1 text-[12px] font-medium text-slate-600">Already a participant?</legend>
          <label className="flex items-center gap-2 text-[13px] text-slate-800">
            <input type="radio" name={`link-${c.id}`} checked={c.link === c.match.client_id} onChange={() => onChange({ link: c.match.client_id })} />
            <Link2 size={13} className="text-slate-400" /> {c.match.name}
            <span className="text-slate-500">({c.match.by === 'mac' ? 'same My Aged Care ID' : c.match.by === 'name_dob' ? 'same name and date of birth' : 'same name: check it is them'})</span>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-800 mt-1">
            <input type="radio" name={`link-${c.id}`} checked={!c.link} onChange={() => onChange({ link: '' })} /> Someone new
          </label>
        </fieldset>
      )}
      <ReferralForm value={c.form} idPrefix={`rv-${c.id}`} onChange={(form) => onChange({ form })} />
      <Contacts contacts={c.contacts} onRemove={(i) => onChange({ contacts: c.contacts.filter((_, j) => j !== i) })} />
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button type="button" onClick={onAdd} disabled={!found || !!c.busy} className="btn-primary btn-sm">
          {c.busy === 'add' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Add to referrals
        </button>
        {aiAvailable && c.can_ai && c.source !== 'ai' && (
          <button type="button" onClick={onAi} disabled={!!c.busy} className="btn-secondary btn-sm"><Sparkles size={14} /> {found ? 'Read again with AI' : 'Read with AI'}</button>
        )}
        <button type="button" onClick={onDiscard} disabled={!!c.busy} className="btn-ghost btn-sm text-slate-600"><Trash2 size={14} /> Discard</button>
      </div>
    </section>
  );
}

// ── A referral typed in by hand ─────────────────────────────────────────────────────────────────
function AddDrawer({ onClose }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', referredDate: isoDate(), program: '', serviceTypes: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) { toast.error('A first and last name are needed'); return; }
    setSaving(true);
    try {
      const r = await api.post('/aged-care/referrals', { fields: forServer(form) });
      toast.success('Referral added'); onClose(r.data.referral.id);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not add the referral'); }
    finally { setSaving(false); }
  };
  return (
    <DetailDrawer open onClose={() => onClose(null)} icon={ListPlus} width="md" title="Add a referral" subtitle="One that came by phone, or with a referral code"
      footer={(
        <>
          <button type="button" onClick={() => onClose(null)} className="btn-secondary">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary"><Check size={15} /> {saving ? 'Adding…' : 'Add referral'}</button>
        </>
      )}>
      <ReferralForm value={form} idPrefix="add" onChange={setForm} />
    </DetailDrawer>
  );
}

// ── One referral: its details and the decision ─────────────────────────────────────────────────
function ReferralDrawer({ id, reasons, aiAvailable, onGoTo, onClose, onChanged }) {
  const confirm = useConfirm();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);       // null | 'edit' | 'accept' | 'waitlist' | 'reject' | 'revoke' | 'recalled'
  const [form, setForm] = useState(null);
  const [act, setAct] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try { const r = await api.get(`/aged-care/referrals/${id}`); setD(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Could not load the referral'); }
  }, [id]);
  useEffect(() => { load(); setMode(null); }, [load]);

  const r = d?.referral;
  const f = r ? fieldsOf(r) : {};
  const today = isoDate();
  const contacts = r?.details?.contacts || [];
  const done = () => { onChanged(); load(); setMode(null); };

  const startAccept = () => {
    setAct({
      program: r.program || '', startDate: r.recommended_start && r.recommended_start > today ? r.recommended_start : today,
      serviceTypeIds: d.services?.ids || [], clientId: r.client_id || (d.match?.certain ? d.match.client_id : 'new'),
      portalDone: false, notes: '', actionDate: today,
    });
    setMode('accept');
  };
  const start = (m) => { setAct({ reason: '', notes: '', portalDone: false, actionDate: today }); setMode(m); };
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'edit') {
        await api.patch(`/aged-care/referrals/${id}`, { fields: forServer(form) });
        toast.success('Saved');
      } else if (mode === 'accept') {
        if (!act.program) { toast.error('Say whether it is CHSP or Support at Home'); setBusy(false); return; }
        const res = await api.post(`/aged-care/referrals/${id}/accept`, act);
        toast.success(res.data.created ? 'Accepted: participant added' : 'Accepted');
        if (res.data.note) toast(res.data.note, { duration: 6000 });
        if (res.data.next) toast(res.data.next, { duration: 8000 });
      } else {
        if ((mode === 'reject' || mode === 'revoke') && !act.reason) { toast.error('Choose the reason'); setBusy(false); return; }
        await api.post(`/aged-care/referrals/${id}/${mode}`, act);
        toast.success({ waitlist: 'Waitlisted', reject: 'Rejected', revoke: 'Revoked', recalled: 'Marked as recalled' }[mode]);
      }
      done();
    } catch (e) {
      const m = e.response?.data;
      // The participant may already be here: offer them.
      if (e.response?.status === 409 && m?.match) {
        setAct((p) => ({ ...p, clientId: m.match.client_id }));
        setD((p) => ({ ...p, match: m.match }));
      }
      toast.error(m?.message || 'Could not save');
    } finally { setBusy(false); }
  };
  const portal = async (value) => {
    try { await api.patch(`/aged-care/referrals/${id}`, { portalDone: value }); onChanged(); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const remove = async () => {
    const ok = await confirm('It is removed from the register. Nothing has been decided on it yet.', { title: 'Remove this referral?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/referrals/${id}`); toast.success('Removed'); onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove it'); }
  };
  const readAi = async () => {
    setBusy(true);
    try { await api.post(`/aged-care/referrals/${id}/read-ai`); toast.success('Read again with AI: check the details'); onChanged(); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'The AI could not read the file'); }
    finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/aged-care/referrals/${id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = r.file_name || 'referral'; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not download the file'); }
  };

  const footer = !r ? null : mode ? (
    <>
      <button type="button" onClick={() => setMode(null)} className="btn-secondary">Back</button>
      <button type="button" onClick={submit} disabled={busy} className={clsx(mode === 'reject' || mode === 'revoke' ? 'btn-danger' : 'btn-primary')}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
        {{ edit: 'Save', accept: 'Accept', waitlist: 'Waitlist', reject: 'Reject', revoke: 'Revoke', recalled: 'Mark as recalled' }[mode]}
      </button>
    </>
  ) : (
    <div className="flex flex-wrap items-center gap-2 w-full justify-end">
      {r.status === 'new' && <button type="button" onClick={remove} className="btn-ghost btn-sm text-slate-600 mr-auto"><Trash2 size={14} /> Remove</button>}
      {r.status === 'waitlisted' && <button type="button" onClick={async () => { try { await api.post(`/aged-care/referrals/${r.id}/waitlist-reviewed`); toast.success('Marked reviewed this week'); if (onChanged) onChanged(); } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); } }} className="btn-ghost btn-sm">Reviewed this week</button>}
      {['new', 'waitlisted'].includes(r.status) && <button type="button" onClick={() => start('recalled')} className="btn-ghost btn-sm"><Undo2 size={14} /> Recalled</button>}
      {['new', 'waitlisted'].includes(r.status) && <button type="button" onClick={() => start('reject')} className="btn-secondary btn-sm"><Ban size={14} /> Reject</button>}
      {r.status === 'new' && <button type="button" onClick={() => start('waitlist')} className="btn-secondary btn-sm"><Hourglass size={14} /> Waitlist</button>}
      {['new', 'waitlisted'].includes(r.status) && <button type="button" onClick={startAccept} className="btn-primary btn-sm"><UserPlus size={14} /> {r.status === 'waitlisted' ? 'Accept into service' : 'Accept'}</button>}
      {r.status === 'accepted' && <button type="button" onClick={() => start('revoke')} className="btn-secondary btn-sm"><Undo2 size={14} /> Revoke</button>}
    </div>
  );

  return (
    <DetailDrawer open onClose={onClose} icon={Inbox} width="lg"
      title={r ? `${r.first_name} ${r.last_name}` : 'Referral'}
      subtitle={r ? [r.program ? PROGRAM[r.program] : 'Program not known', r.referral_code ? `referral code ${r.referral_code}` : null].filter(Boolean).join(' · ') : null}
      statusNode={r ? <StatusBadge status={r.status} /> : null}
      footer={footer}>
      {error && <p className="text-[13px] text-red-700">{error}</p>}
      {!r && !error && <p className="text-[13px] text-slate-500">Loading…</p>}
      {r && !mode && (
        <div className="space-y-5">
          {r.status === 'new' && (
            <div className={clsx('rounded-lg px-3 py-2.5 text-[13px]', r.due_date && r.due_date < today ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900')}>
              <p className="font-medium">Respond in My Aged Care: <Deadline date={r.due_date} className="!text-inherit" /></p>
              <p className="text-[12px] mt-0.5 opacity-90">Accept, waitlist or reject it in the Service and Support Portal (a Team Leader), then record it here.{r.due_estimated ? ' The date is 3 calendar days after it was referred.' : ''}</p>
            </div>
          )}
          {['accepted', 'waitlisted', 'rejected', 'revoked', 'recalled'].includes(r.status) && (
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] text-slate-700 space-y-1.5">
              <p><span className="font-medium text-slate-900">{STATUS[r.status][0]}</span> on {fmtDate(r.action_date)}{r.actioned_by_name ? ` by ${r.actioned_by_name}` : ''}{r.action_reason ? `: ${r.action_reason}` : ''}</p>
              {r.action_notes && <p className="text-slate-600">{r.action_notes}</p>}
              {r.status !== 'recalled' && (
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={!!r.portal_done} onChange={(e) => portal(e.target.checked)} />
                  <span className={r.portal_done ? 'text-slate-700' : 'text-amber-800 font-medium'}>Done in the My Aged Care portal too</span>
                </label>
              )}
              {r.status === 'accepted' && r.program === 'sah' && !r.has_funding && (
                <p className="flex items-start gap-2 text-amber-800"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> Set up their Support at Home funding plan.{onGoTo && <button type="button" className="ml-1 underline font-medium" onClick={() => onGoTo('funding')}>Client funding</button>}</p>
              )}
            </div>
          )}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            <Item label="Participant">{r.client_name
              ? <span className="text-slate-800">{r.client_name}{r.client_active === false ? ' (inactive)' : ''}</span>
              : d.match ? <span className="text-amber-800">Possibly {d.match.name}</span> : <span className="text-slate-500">Someone new</span>}</Item>
            <Item label="My Aged Care ID">{r.mac_id || <span className="text-amber-700">Not recorded</span>}</Item>
            <Item label="Date of birth">{r.date_of_birth ? fmtDate(r.date_of_birth) : '—'}</Item>
            <Item label="Priority">{r.priority ? <span className={PRIORITY[r.priority]}>{cap(r.priority)}</span> : '—'}</Item>
            <Item label="Referred on">{r.referred_date ? fmtDate(r.referred_date) : '—'}</Item>
            <Item label="Recommended start">{r.recommended_start ? fmtDate(r.recommended_start) : '—'}</Item>
            {r.program === 'sah' && <Item label="Classification">{f.classification || '—'}</Item>}
            {r.take_up_by && <Item label="Take-up deadline"><Deadline date={r.take_up_by} /></Item>}
            <Item label="Services" wide>{r.service_types?.length ? r.service_types.join(', ') : '—'}</Item>
            {r.program === 'chsp' && d.services?.unmatched?.length > 0 && (
              <Item label="" wide><span className="text-amber-800 text-[12px]">Not set up as a CHSP service here: {d.services.unmatched.join(', ')}. Set it up under CHSP, Services and targets.</span></Item>
            )}
            {r.frequency && <Item label="Frequency and intensity" wide>{r.frequency}</Item>}
            {r.comments && <Item label="Referral comments" wide><span className="whitespace-pre-line">{r.comments}</span></Item>}
            <Item label="Phone">{f.phone || f.mobile || '—'}</Item>
            <Item label="Email">{f.email || '—'}</Item>
            <Item label="Address" wide>{[f.address, f.suburb, f.state, f.postcode].filter(Boolean).join(', ') || '—'}</Item>
            {(f.language || f.interpreterRequired) && <Item label="Language">{[f.language, f.interpreterRequired ? 'needs an interpreter' : null].filter(Boolean).join(', ')}</Item>}
            {r.naps_service_id && <Item label="NAPS service ID">{r.naps_service_id}</Item>}
          </dl>
          <Contacts contacts={contacts} />
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-[12px] text-slate-500">
            {r.file_name
              ? <button type="button" onClick={download} className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 font-medium"><Download size={13} /> {r.file_name}</button>
              : <span>Added by hand</span>}
            <span>{r.read_by === 'ai' ? '· read by AI' : r.read_by === 'person' ? '· checked by a person' : r.read_by === 'reader' ? '· read by Rostery' : ''}</span>
            {['new', 'waitlisted'].includes(r.status) && (
              <span className="ml-auto flex items-center gap-2">
                {aiAvailable && r.file_name && <button type="button" onClick={readAi} disabled={busy} className="btn-ghost btn-sm"><Sparkles size={13} /> Read again with AI</button>}
                <button type="button" onClick={() => { setForm(fieldsOf(r)); setMode('edit'); }} className="btn-ghost btn-sm"><PencilLine size={13} /> Edit details</button>
              </span>
            )}
          </div>
        </div>
      )}
      {r && mode === 'edit' && form && <ReferralForm value={form} idPrefix={`ed-${id}`} onChange={setForm} />}
      {r && mode === 'accept' && <AcceptForm r={r} d={d} act={act} setAct={setAct} />}
      {r && ['waitlist', 'reject', 'revoke', 'recalled'].includes(mode) && (
        <DecisionForm mode={mode} r={r} act={act} setAct={setAct} reasons={mode === 'reject' ? reasons.reject : mode === 'revoke' ? reasons.revoke : []} />
      )}
    </DetailDrawer>
  );
}

function Item({ label, wide, children }) {
  return (
    <div className={clsx('min-w-0', wide && 'sm:col-span-2')}>
      {label && <dt className="text-[12px] text-slate-500">{label}</dt>}
      <dd className="text-slate-800 break-words">{children}</dd>
    </div>
  );
}

function PortalTick({ act, setAct, word }) {
  return (
    <label className="flex items-start gap-2 text-[13px] text-slate-700">
      <input type="checkbox" className="mt-0.5" checked={!!act.portalDone} onChange={(e) => setAct((p) => ({ ...p, portalDone: e.target.checked }))} />
      <span>I have {word} it in the My Aged Care portal <span className="text-slate-500">(leave unticked to be reminded)</span></span>
    </label>
  );
}

function AcceptForm({ r, d, act, setAct }) {
  const services = d.chsp_services || [];
  const toggle = (sid) => setAct((p) => ({ ...p, serviceTypeIds: p.serviceTypeIds.includes(sid) ? p.serviceTypeIds.filter((x) => x !== sid) : [...p.serviceTypeIds, sid] }));
  return (
    <div className="space-y-4">
      {!r.program && (
        <Field label="Program" htmlFor="acc-program">
          <select id="acc-program" className="input-field" value={act.program} onChange={(e) => setAct((p) => ({ ...p, program: e.target.value }))}>
            <option value="">Choose…</option><option value="chsp">CHSP</option><option value="sah">Support at Home</option>
          </select>
        </Field>
      )}
      <fieldset>
        <legend className="text-[13px] font-medium text-slate-700 mb-1">Participant</legend>
        {r.client_id ? (
          <p className="text-[13px] text-slate-800">{r.client_name}</p>
        ) : (
          <div className="space-y-1">
            {d.match && (
              <label className="flex items-center gap-2 text-[13px] text-slate-800">
                <input type="radio" name="acc-client" checked={act.clientId === d.match.client_id} onChange={() => setAct((p) => ({ ...p, clientId: d.match.client_id }))} />
                {d.match.name} <span className="text-slate-500">({d.match.by === 'mac' ? 'same My Aged Care ID' : d.match.by === 'name_dob' ? 'same name and date of birth' : 'same name'})</span>
              </label>
            )}
            <label className="flex items-center gap-2 text-[13px] text-slate-800">
              <input type="radio" name="acc-client" checked={act.clientId === 'new'} onChange={() => setAct((p) => ({ ...p, clientId: 'new' }))} />
              Add {r.first_name} {r.last_name} as a new aged care participant
            </label>
          </div>
        )}
      </fieldset>
      <FormRow cols={2}>
        <Field label="Accepted on" htmlFor="acc-date">
          <input id="acc-date" type="date" className="input-field" value={act.actionDate} onChange={(e) => setAct((p) => ({ ...p, actionDate: e.target.value }))} />
        </Field>
        {(act.program || r.program) === 'chsp' && (
          <Field label="CHSP services start" htmlFor="acc-start">
            <input id="acc-start" type="date" className="input-field" value={act.startDate} onChange={(e) => setAct((p) => ({ ...p, startDate: e.target.value }))} />
          </Field>
        )}
      </FormRow>
      {(act.program || r.program) === 'chsp' && (
        <fieldset>
          <legend className="text-[13px] font-medium text-slate-700 mb-1">Enrol in CHSP for</legend>
          <p className="text-[12px] text-slate-500 mb-2">Ticked from the referred services. Leave all unticked to allow any CHSP service.</p>
          {services.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {services.map((s) => (
                <label key={s.service_type_id} className="flex items-center gap-2 text-[13px] text-slate-800">
                  <input type="checkbox" checked={act.serviceTypeIds.includes(s.service_type_id)} onChange={() => toggle(s.service_type_id)} />
                  <span className="truncate">{s.name}{s.chsp_service && s.chsp_service !== s.name ? <span className="text-slate-500"> ({s.chsp_service})</span> : null}</span>
                </label>
              ))}
            </div>
          ) : <p className="text-[12px] text-amber-700">No CHSP services are set up yet. They will be enrolled for any CHSP service; set the services up under CHSP.</p>}
          {d.services?.unmatched?.length > 0 && <p className="mt-2 text-[12px] text-amber-800">Referred for, but not set up here: {d.services.unmatched.join(', ')}.</p>}
        </fieldset>
      )}
      {(act.program || r.program) === 'sah' && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
          Their Support at Home funding plan records the referral and acceptance dates. If they have no plan yet, you will be reminded to set one up{r.take_up_by ? `; services must start by ${fmtDate(r.take_up_by)}` : ''}.
        </p>
      )}
      <Field label="Notes" htmlFor="acc-notes">
        <textarea id="acc-notes" rows={2} className="input-field" value={act.notes} onChange={(e) => setAct((p) => ({ ...p, notes: e.target.value }))} />
      </Field>
      <PortalTick act={act} setAct={setAct} word="accepted" />
    </div>
  );
}

function DecisionForm({ mode, r, act, setAct, reasons }) {
  const word = { waitlist: 'waitlisted', reject: 'rejected', revoke: 'revoked', recalled: 'recalled' }[mode];
  return (
    <div className="space-y-4">
      {mode === 'revoke' && <p className="text-[13px] text-slate-600">My Aged Care only allows this while services have not started. {r.program === 'chsp' ? 'The CHSP enrolment made when it was accepted is removed.' : ''}</p>}
      {mode === 'recalled' && <p className="text-[13px] text-slate-600">My Aged Care withdrew the referral: the client chose another provider, or it lapsed.</p>}
      {mode === 'waitlist' && <p className="text-[13px] text-slate-600">Only when your outlet offers a waitlist for the service. Accept them into service when a place comes up.</p>}
      {reasons.length > 0 && (
        <Field label="Reason" htmlFor="dec-reason">
          <select id="dec-reason" className="input-field" value={act.reason} onChange={(e) => setAct((p) => ({ ...p, reason: e.target.value }))}>
            <option value="">Choose…</option>{reasons.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
      )}
      <FormRow cols={2}>
        <Field label="On" htmlFor="dec-date">
          <input id="dec-date" type="date" className="input-field" value={act.actionDate} onChange={(e) => setAct((p) => ({ ...p, actionDate: e.target.value }))} />
        </Field>
      </FormRow>
      <Field label={act.reason === 'Other' ? 'What the reason is' : 'Notes'} htmlFor="dec-notes">
        <textarea id="dec-notes" rows={2} className="input-field" value={act.notes} onChange={(e) => setAct((p) => ({ ...p, notes: e.target.value }))} />
      </Field>
      {mode !== 'recalled' && <PortalTick act={act} setAct={setAct} word={word} />}
    </div>
  );
}
